import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api/client'
import { useResource, useToast } from '../api/hooks'
import type { DoorListItem, Group, SchedulePreview, ScheduleRule } from '../api/types'
import { PageHeader } from '../components/Layout'
import {
  ALL_DAYS, ScheduleGrid, ScheduleMini, ScheduleRuleList, summarizeSchedule,
} from '../components/ScheduleGrid'
import {
  Alert, Card, ConfirmDialog, ErrorBox, Field, Spinner, Toast, describeError,
} from '../components/Ui'
import { de, fmt } from '../i18n/de'

const TEMPLATES: Record<string, ScheduleRule[]> = {
  office: [{ dayMask: 0b0011111, startMinute: 420, endMinute: 1140 }],
  school: [{ dayMask: 0b0011111, startMinute: 450, endMinute: 1000 }],
  evening: [
    { dayMask: 0b0011111, startMinute: 1020, endMinute: 1380 },
    { dayMask: 0b0100000, startMinute: 540, endMinute: 720 },
  ],
}

export function GroupsPage() {
  const navigate = useNavigate()
  const groups = useResource<Group[]>('/groups')
  const [showCreate, setShowCreate] = useState(false)
  const { toast, show, clear } = useToast()

  if (groups.loading) return <Spinner />
  if (groups.error) return <ErrorBox error={groups.error} onRetry={groups.reload} />

  return (
    <>
      <PageHeader title={de.groups.title} subtitle={de.groups.intro}
                  actions={<button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                    {de.groups.create}
                  </button>} />

      {(groups.data ?? []).length === 0 && (
        <Alert kind="info">{de.app.noEntries}</Alert>
      )}

      <div className="group-cards">
        {(groups.data ?? []).map(group => (
          <div className="card group-card" key={group.id} onClick={() => navigate(`/groups/${group.id}`)}>
            <div className="card-b">
              <h3>
                <span style={{ color: group.color }}>⬡</span> {group.name}
              </h3>
              <div className="xs muted">
                {fmt(de.groups.counts, { codes: group.doorCodeCount, doors: group.doorIDs.length })}
              </div>

              <ScheduleMini rules={group.rules} color={group.color} isAlwaysOpen={group.isAlwaysOpen} />

              <div className="xs" style={{ marginTop: 'var(--sp-2)' }}>
                <strong>{summarizeSchedule(group.rules, group.isAlwaysOpen)}</strong>
              </div>
              {group.description && (
                <div className="xs muted" style={{ marginTop: 'var(--sp-1)' }}>{group.description}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <CreateGroupDialog
          onClose={() => setShowCreate(false)}
          onCreated={id => { groups.reload(); navigate(`/groups/${id}`) }}
          onError={message => show(message, 'warn')} />
      )}

      {toast && <Toast message={toast.message} kind={toast.kind} onDone={clear} />}
    </>
  )
}

function CreateGroupDialog({ onClose, onCreated, onError }: {
  onClose: () => void
  onCreated: (id: string) => void
  onError: (message: string) => void
}) {
  const doors = useResource<DoorListItem[]>('/doors')
  const [name, setName] = useState('')
  const [alwaysOpen, setAlwaysOpen] = useState(false)
  const [doorIDs, setDoorIDs] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true)
    try {
      const group = await api.post<Group>('/groups', {
        name,
        isAlwaysOpen: alwaysOpen,
        doorIDs: [...doorIDs],
        rules: alwaysOpen ? [] : TEMPLATES.office,
      })
      onCreated(group.id)
    } catch (error) {
      onError(describeError(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgb(0 0 0 / .4)',
      display: 'grid', placeItems: 'center', zIndex: 60,
    }} role="dialog" aria-modal="true">
      <div className="card" style={{ width: 440, boxShadow: 'var(--shadow-2)' }}>
        <div className="card-h">{de.groups.create}</div>
        <div className="card-b">
          <Field label={de.groups.name}>
            <input type="text" value={name} autoFocus onChange={event => setName(event.target.value)} />
          </Field>

          <label className="sm" style={{ display: 'flex', gap: 6, marginBottom: 'var(--sp-3)' }}>
            <input type="checkbox" checked={alwaysOpen}
                   onChange={event => setAlwaysOpen(event.target.checked)} />
            {de.groups.alwaysOpen}
          </label>

          <Field label={de.groups.doors}>
            <div style={{ display: 'grid', gap: 'var(--sp-1)' }}>
              {(doors.data ?? []).map(door => (
                <label key={door.id} className="sm" style={{ display: 'flex', gap: 6 }}>
                  <input type="checkbox" checked={doorIDs.has(door.id)}
                         onChange={event => setDoorIDs(current => {
                           const next = new Set(current)
                           if (event.target.checked) next.add(door.id)
                           else next.delete(door.id)
                           return next
                         })} />
                  {door.name}
                  {door.location && <span className="faint">· {door.location}</span>}
                </label>
              ))}
            </div>
          </Field>

          <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>{de.app.cancel}</button>
            <button className="btn btn-primary" disabled={busy || name.trim().length === 0}
                    onClick={() => void submit()}>
              {de.groups.create}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function GroupDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast, show, clear } = useToast()

  const group = useResource<Group>(`/groups/${id}`, [id])
  const doors = useResource<DoorListItem[]>('/doors')
  const allGroups = useResource<Group[]>('/groups')

  const [rules, setRules] = useState<ScheduleRule[] | null>(null)
  const [alwaysOpen, setAlwaysOpen] = useState<boolean | null>(null)
  const [preview, setPreview] = useState<SchedulePreview | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  if (group.loading) return <Spinner />
  if (group.error) return <ErrorBox error={group.error} onRetry={group.reload} />
  if (!group.data) return <ErrorBox error={new Error('not found')} />

  const detail = group.data
  const effectiveRules = rules ?? detail.rules
  const effectiveAlways = alwaysOpen ?? detail.isAlwaysOpen
  const dirty = rules !== null || alwaysOpen !== null

  const setSchedule = async () => {
    try {
      await api.put(`/groups/${id}/schedule`, {
        isAlwaysOpen: effectiveAlways,
        rules: effectiveAlways ? [] : effectiveRules,
      })
      setRules(null)
      setAlwaysOpen(null)
      setPreview(null)
      group.reload()
      show(de.app.save)
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  /** Probelauf vor dem Speichern: eine Planänderung wirkt auf dutzende Personen. */
  const runPreview = async () => {
    try {
      setPreview(await api.post<SchedulePreview>(`/groups/${id}/schedule/preview`, {
        isAlwaysOpen: effectiveAlways,
        rules: effectiveAlways ? [] : effectiveRules,
      }))
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  const setDoors = async (next: Set<string>) => {
    try {
      await api.put(`/groups/${id}/doors`, { doorIDs: [...next] })
      group.reload()
      show(de.app.save)
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  const remove = async () => {
    try {
      await api.delete(`/groups/${id}`)
      navigate('/groups')
    } catch (error) {
      setConfirmDelete(false)
      show(describeError(error), 'warn')
    }
  }

  const doorNames = (doors.data ?? [])
    .filter(door => detail.doorIDs.includes(door.id))
    .map(door => door.name)
    .join(', ')

  return (
    <>
      <PageHeader
        title={detail.name}
        back={{ to: '/groups', label: de.groups.title }}
        subtitle={fmt(de.groups.counts, {
          codes: detail.doorCodeCount, doors: detail.doorIDs.length,
        })}
        actions={<>
          <button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>
            {de.app.delete}
          </button>
          {dirty && (
            <button className="btn btn-primary" onClick={() => void setSchedule()}>
              {de.app.save}
            </button>
          )}
        </>} />

      <div style={{ marginBottom: 'var(--sp-4)' }}>
        <Card>
          <div className="grid cols-2" style={{ gap: 'var(--sp-3)' }}>
            <Field label={de.groups.name}>
              <input type="text" defaultValue={detail.name}
                     onBlur={async event => {
                       if (event.target.value === detail.name) return
                       try {
                         await api.patch(`/groups/${id}`, { name: event.target.value })
                         group.reload()
                       } catch (error) { show(describeError(error), 'warn') }
                     }} />
            </Field>
            <Field label={de.groups.description}>
              <input type="text" defaultValue={detail.description ?? ''}
                     onBlur={async event => {
                       try {
                         await api.patch(`/groups/${id}`, { description: event.target.value })
                         group.reload()
                       } catch (error) { show(describeError(error), 'warn') }
                     }} />
            </Field>
          </div>
        </Card>
      </div>

      <div style={{ marginBottom: 'var(--sp-4)' }}>
        <Card title={de.groups.doors}>
          <table><tbody>
            {(doors.data ?? []).map(door => {
              const others = (allGroups.data ?? [])
                .filter(other => other.id !== id && other.doorIDs.includes(door.id))
                .map(other => other.name)

              return (
                <tr key={door.id}>
                  <td style={{ width: 28 }}>
                    <input type="checkbox" checked={detail.doorIDs.includes(door.id)}
                           aria-label={door.name}
                           onChange={event => {
                             const next = new Set(detail.doorIDs)
                             if (event.target.checked) next.add(door.id)
                             else next.delete(door.id)
                             void setDoors(next)
                           }} />
                  </td>
                  <td><strong>{door.name}</strong></td>
                  <td className="muted">{door.location}</td>
                  <td className="xs muted">
                    {others.length > 0 ? fmt(de.groups.alsoIn, { groups: others.join(', ') }) : ''}
                  </td>
                </tr>
              )
            })}
          </tbody></table>
        </Card>
      </div>

      <div style={{ marginBottom: 'var(--sp-4)' }}>
        <Card title={de.groups.schedule} actions={<>
          <label className="sm" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={effectiveAlways}
                   onChange={event => setAlwaysOpen(event.target.checked)} />
            {de.groups.alwaysOpen}
          </label>
          {!effectiveAlways && <>
            <span className="xs muted">{de.groups.templates}:</span>
            <button className="btn btn-ghost" onClick={() => setRules(TEMPLATES.school!)}>
              {de.groups.templateSchool}
            </button>
            <button className="btn btn-ghost" onClick={() => setRules(TEMPLATES.office!)}>
              {de.groups.templateOffice}
            </button>
            <button className="btn btn-ghost" onClick={() => setRules(TEMPLATES.evening!)}>
              {de.groups.templateEvening}
            </button>
          </>}
        </>}>
          {effectiveAlways ? (
            <Alert kind="info">{de.groups.alwaysOpenHint}</Alert>
          ) : (
            <>
              <ScheduleGrid
                bands={[{ rules: effectiveRules, color: detail.color }]}
                onChange={setRules} />

              <div className="sched-legend"><span>{de.groups.gridHint}</span></div>

              <ScheduleRuleList rules={effectiveRules} onChange={setRules} />

              <div className="help" style={{ marginTop: 'var(--sp-2)' }}>{de.groups.listHint}</div>

              {dirty && (
                <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
                  <button className="btn btn-ghost" onClick={() => void runPreview()}>
                    {de.groups.impact} prüfen
                  </button>
                  <button className="btn btn-primary" onClick={() => void setSchedule()}>
                    {de.app.save}
                  </button>
                  <button className="btn btn-ghost"
                          onClick={() => { setRules(null); setAlwaysOpen(null); setPreview(null) }}>
                    {de.app.cancel}
                  </button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {preview && (
        <Card title={de.groups.impact}>
          <div className="sm">
            {fmt(de.groups.impactUsers, {
              codes: preview.affectedDoorCodes, narrowed: preview.narrowedDoorCodes,
            })}
          </div>

          {preview.losingAccess.length === 0 && preview.gainingAccess.length === 0 && (
            <div style={{ marginTop: 'var(--sp-3)' }}>
              <Alert kind="info">{de.groups.impactNone}</Alert>
            </div>
          )}

          {preview.losingAccess.length > 0 && (
            <div style={{ marginTop: 'var(--sp-3)' }}>
              <Alert kind="warning">
                <strong>{fmt(de.groups.impactLosing, { count: preview.losingAccess.length })}</strong>
                <ul style={{ margin: 'var(--sp-2) 0 0', paddingLeft: 'var(--sp-5)' }}>
                  {preview.losingAccess.slice(0, 12).map((change, index) => (
                    <li key={index} className="sm">
                      {change.name} — {change.doorName}: {change.windows.join(', ')}
                    </li>
                  ))}
                </ul>
              </Alert>
            </div>
          )}

          {preview.gainingAccess.length > 0 && (
            <div style={{ marginTop: 'var(--sp-3)' }}>
              <Alert kind="info">
                {fmt(de.groups.impactGaining, { count: preview.gainingAccess.length })}
              </Alert>
            </div>
          )}
        </Card>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={fmt(de.groups.confirmDelete, { name: detail.name })}
          detail={fmt(de.groups.confirmDeleteDetail, {
            codes: detail.doorCodeCount,
            doors: doorNames || de.app.none,
          })}
          confirmLabel={de.app.delete}
          onConfirm={() => void remove()}
          onCancel={() => setConfirmDelete(false)} />
      )}

      {toast && <Toast message={toast.message} kind={toast.kind} onDone={clear} />}
    </>
  )
}

/** Wochengitter einer Tür mit überlagerten Gruppenbändern. */
export function DoorScheduleBands({ bands }: {
  bands: Array<{ groupID: string; groupName: string; color: string; isAlwaysOpen: boolean; rules: ScheduleRule[] }>
}) {
  return (
    <ScheduleGrid
      rowHeight={16}
      bands={bands.map(band => ({
        rules: band.isAlwaysOpen
          ? [{ dayMask: ALL_DAYS, startMinute: 0, endMinute: 1440 }]
          : band.rules,
        color: band.color,
        label: band.groupName,
      }))} />
  )
}
